import { useState, useEffect } from "react";
import { 
  BookOpen, 
  Plus, 
  Trash2, 
  Edit, 
  ExternalLink,
  Save,
  Loader2,
  X,
  Calendar
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";

export default function BlogEditor() {
  const { getAuthHeaders } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPost, setNewPost] = useState({ title: "", slug: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/all`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error("Failed to load posts:", error);
      toast.error("Kunde inte ladda inlägg");
    } finally {
      setLoading(false);
    }
  };

  const createPost = async (e) => {
    e.preventDefault();
    if (!newPost.title || !newPost.slug) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/blog`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(newPost)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Inlägget skapades!");
        setShowCreateModal(false);
        setNewPost({ title: "", slug: "" });
        loadPosts();
        setSelectedPost(data.post);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Kunde inte skapa inlägget");
      }
    } catch (error) {
      toast.error("Något gick fel");
    } finally {
      setSaving(false);
    }
  };

  const savePost = async () => {
    if (!selectedPost) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/blog/${selectedPost.slug}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          title: selectedPost.title,
          excerpt: selectedPost.excerpt,
          content: selectedPost.content,
          meta_title: selectedPost.meta_title,
          meta_description: selectedPost.meta_description,
          featured_image: selectedPost.featured_image,
          featured_image_alt: selectedPost.featured_image_alt,
          status: selectedPost.status
        })
      });

      if (response.ok) {
        toast.success("Inlägget sparades!");
        loadPosts();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Kunde inte spara");
      }
    } catch (error) {
      toast.error("Något gick fel");
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (slug) => {
    if (!window.confirm("Är du säker på att du vill radera detta inlägg?")) return;

    try {
      const response = await fetch(`${API_URL}/api/blog/${slug}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Inlägget raderades!");
        if (selectedPost?.slug === slug) setSelectedPost(null);
        loadPosts();
      }
    } catch (error) {
      toast.error("Kunde inte radera");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
            <BookOpen className="w-6 sm:w-7 h-6 sm:h-7 text-[#255E69]" />
            Blogg
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Skapa och redigera blogginlägg.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#255E69] text-white rounded-xl hover:bg-[#1e4a53] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nytt inlägg
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Posts List */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Inlägg</h2>
          </div>
          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {posts.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                Inga inlägg ännu. Skapa ditt första!
              </div>
            ) : (
              posts.map((post) => (
                <div
                  key={post.slug}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedPost?.slug === post.slug ? "bg-[#255E69]/5 border-l-2 border-[#255E69]" : ""
                  }`}
                  onClick={() => setSelectedPost(post)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{post.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500">{formatDate(post.created_at)}</span>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                      post.status === "published" 
                        ? "bg-green-100 text-green-700" 
                        : "bg-gray-100 text-gray-600"
                    }`}>
                      {post.status === "published" ? "Publicerad" : "Utkast"}
                    </span>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <a
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-[#255E69] hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Visa
                    </a>
                    <button
                      onClick={(e) => { e.stopPropagation(); deletePost(post.slug); }}
                      className="text-xs text-red-500 hover:underline flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Radera
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Post Editor */}
        <div className="lg:col-span-2">
          {selectedPost ? (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Editor Header */}
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900 truncate">{selectedPost.title}</h2>
                <div className="flex gap-2">
                  <select
                    value={selectedPost.status}
                    onChange={(e) => setSelectedPost(prev => ({ ...prev, status: e.target.value }))}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="draft">Utkast</option>
                    <option value="published">Publicerad</option>
                  </select>
                  <button
                    onClick={savePost}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-1.5 bg-[#255E69] text-white rounded-lg text-sm hover:bg-[#1e4a53] disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Spara
                  </button>
                </div>
              </div>

              {/* Editor Content */}
              <div className="p-4 space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                  <input
                    type="text"
                    value={selectedPost.title}
                    onChange={(e) => setSelectedPost(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Utdrag (kort beskrivning)</label>
                  <textarea
                    value={selectedPost.excerpt || ""}
                    onChange={(e) => setSelectedPost(prev => ({ ...prev, excerpt: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Innehåll</label>
                  <textarea
                    value={selectedPost.content || ""}
                    onChange={(e) => setSelectedPost(prev => ({ ...prev, content: e.target.value }))}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg font-mono text-sm"
                    placeholder="Skriv ditt inlägg här..."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Tips: Använd radbrytningar för stycken. HTML stöds inte.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Utvald bild (URL)</label>
                    <input
                      type="text"
                      value={selectedPost.featured_image || ""}
                      onChange={(e) => setSelectedPost(prev => ({ ...prev, featured_image: e.target.value }))}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bild alt-text</label>
                    <input
                      type="text"
                      value={selectedPost.featured_image_alt || ""}
                      onChange={(e) => setSelectedPost(prev => ({ ...prev, featured_image_alt: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <h3 className="font-medium text-gray-900 mb-3">SEO</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                      <input
                        type="text"
                        value={selectedPost.meta_title || ""}
                        onChange={(e) => setSelectedPost(prev => ({ ...prev, meta_title: e.target.value }))}
                        placeholder="Titel | Elido Blogg"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                      <textarea
                        value={selectedPost.meta_description || ""}
                        onChange={(e) => setSelectedPost(prev => ({ ...prev, meta_description: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">Välj ett inlägg</h3>
              <p className="text-gray-500 text-sm">
                Välj ett inlägg från listan till vänster för att redigera det.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Skapa nytt inlägg</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={createPost} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Min första bloggpost"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL (slug)</label>
                <div className="flex items-center">
                  <span className="text-gray-400 mr-1">/blog/</span>
                  <input
                    type="text"
                    value={newPost.slug}
                    onChange={(e) => setNewPost(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                    placeholder="min-forsta-bloggpost"
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-lg"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#255E69] text-white rounded-lg font-medium hover:bg-[#1e4a53] disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                  Skapa inlägg
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
